/**
 * buildCalendarPermissionsForOrg — parameterized version of
 * `useCalendarPermissions` that resolves permissions for any org's
 * `accessGroups` (not just the bootstrapped current-org one).
 *
 * Normalizes the `accessGroups` shape at the read boundary. Backend ships a
 * **41-item array of permission name strings** (verified by Redux state
 * inspection — Brainstormer round 3 finding):
 *   `["dashboard", "calendarView", "calendarManageMy", ...]`
 * The downstream `buildCalendarPermissions` reads the input as a boolean
 * map (`accessGroups.calendarManageMy`). On an array, every permission
 * evaluates to `false` → Edit / Delete buttons stay hidden, recurring
 * scope dialog never opens. We convert array → `{ [name]: true }` here so
 * BOTH the current-org and cross-org reads (via `eventOrgPerms`) work.
 *
 * Object inputs are passed through untouched (defensive — backend may
 * eventually ship the boolean map, or AccessGroupsProvider may normalize
 * upstream; either path stays correct).
 *
 * @module buildCalendarPermissionsForOrg
 */
import { buildCalendarPermissions } from '../hooks/useCalendarPermissions.js';
import { normalizeAccessGroups } from './normalizeAccessGroups.js';

// Re-export so existing call sites that import `normalizeAccessGroups` from
// here continue to resolve. The implementation now lives in a standalone
// dependency-free module so `eventPermissions.js` can use it without
// pulling React/HeroUI transitively via this file's hook import.
export { normalizeAccessGroups };

/**
 * @param {Object|Array} accessGroups Either:
 *   - boolean map `{ calendarManageMy: true, ... }` (legacy / direct DTO)
 *   - or string array `["calendarManageMy", ...]` (live backend shape)
 *   typically from `state.calendarMonthBootstrap.eventOrgPerms[orgUnique]`
 *   or `state.calendarMonthBootstrap.accessGroups` for the current org.
 * @returns Same shape as `useCalendarPermissions()`.
 */
export function buildCalendarPermissionsForOrg(accessGroups) {
	return buildCalendarPermissions(normalizeAccessGroups(accessGroups));
}
